/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential.
 *
 * ONE PERSON UNDER TWO NAMES THE FILE ITSELF LINKS (v1_812).
 *
 * The shared name matcher (person-name-match.js samePerson) tolerates the ways
 * one name varies in form. It cannot know that "Eddie Fennessy" is "Edward
 * Francis FENNESSY": a different first name is, by rule, a different person. On
 * Fig Markets (production, 25/09/26) the file said so itself, in the role text
 * of the verified director ("also known as Eddie Fennessy"), yet an old link row
 * under the short name was listed as a second person beside him.
 *
 * fileMatcher(report, collected, base) is the matcher every identity surface
 * uses: the shared rule, OR a link the file records between the two names:
 *   an alias recorded on any party entry: "known as X", "also known as X",
 *     "aka X", "formerly known as X", "nee X", or an aliases / knownAs field;
 *   an officer's "Same person as" merge (collected_data.personLinks), recorded
 *     with a case note by the identity panel control.
 * A single word alias ("known as Eddie") takes the entry's surname. Pure.
 */
import { samePerson as baseSamePerson } from './person-name-match.js';

const ALIAS_RE = /\b(?:also\s+known\s+as|formerly\s+known\s+as|known\s+as|a\.?k\.?a\.?|n[ée]e)\s*[:,]?\s*["'“‘]?([A-Za-zÀ-ɏ][A-Za-zÀ-ɏ.'\s]{0,60}?)["'”’]?(?=\s*(?:$|[,;()\]]|\s(?:and|or|who|is|was|the)\b))/gi;
const LISTS = ['directors', 'ubos', 'authorisedSignatories', 'sumsubRequired', 'shareholders'];
const nameOf = (p) => (p && typeof p === 'object') ? p.name : String(p || '').split(' — ')[0];
const tokens = (n) => String(n || '').trim().split(/\s+/).filter(Boolean);

// Aliases one entry records for itself.
export function aliasesOfEntry(entry) {
  if (!entry || typeof entry !== 'object') return [];
  const out = [];
  const texts = ['role', 'notes', 'name', 'description', 'detail'].map(k => typeof entry[k] === 'string' ? entry[k] : '');
  for (const t of texts) {
    let m; const re = new RegExp(ALIAS_RE.source, 'gi');
    while ((m = re.exec(t))) out.push(m[1].trim());
  }
  // v1_888: alsoListedAs is what the owner and director row merge records for the
  // name forms it folded into the kept row, so one merge is honoured everywhere.
  for (const k of ['aliases', 'aka', 'knownAs', 'alsoKnownAs', 'otherNames', 'alsoListedAs']) {
    const v = entry[k];
    if (Array.isArray(v)) out.push(...v.filter(x => typeof x === 'string'));
    else if (typeof v === 'string' && v.trim()) out.push(...v.split(/[,;]/));
  }
  const own = tokens(String(entry.name || '').replace(/\([^)]*\)/g, ' '));
  const surname = own.length > 1 ? own[own.length - 1] : '';
  return [...new Set(out.map(a => a.replace(/\s+/g, ' ').trim()).filter(Boolean)
    .map(a => (tokens(a).length === 1 && surname) ? a + ' ' + surname : a))];
}

// Every name pair the file links: [recorded name, alias] and the officer's merges.
export function aliasPairsFromFile(report, collected) {
  const r = (report && typeof report === 'object') ? report : {};
  const cd = (collected && typeof collected === 'object') ? collected : {};
  const pairs = [];
  const entries = [];
  for (const l of LISTS) for (const p of (Array.isArray(r[l]) ? r[l] : [])) if (p && typeof p === 'object') entries.push(p);
  for (const c of Object.values(cd.carriedPeople || {})) if (c && c.entry) entries.push({ ...c.entry, name: c.name || c.entry.name });
  for (const e of entries) {
    const nm = nameOf(e);
    const clean = String(nm || '').replace(/\([^)]*\)/g, ' ').replace(/\s+/g, ' ').trim();
    for (const a of aliasesOfEntry(e)) if (clean && a) pairs.push([clean, a]);
  }
  for (const l of Object.values(cd.personLinks || {})) if (l && l.a && l.b) pairs.push([l.a, l.b]);
  return pairs;
}

// The file aware matcher. base(a, b), or both names sit on the two ends of a
// link the file records (in either direction, one hop).
export function fileMatcher(report, collected, base = baseSamePerson) {
  const pairs = aliasPairsFromFile(report, collected);
  if (!pairs.length) return base;
  return (a, b) => {
    if (base(a, b)) return true;
    for (const [x, y] of pairs) {
      if ((base(a, x) && base(b, y)) || (base(a, y) && base(b, x))) return true;
    }
    return false;
  };
}
